/**
 * Contact + feedback modal. Ported from oakallow-react-vite's
 * ContactFeedbackModal — same flow (four types, message body, optional
 * email if not signed in, "I'd like a response" checkbox, success
 * state with reset). Tailwind classes instead of CSS modules to match
 * the rest of the PWA.
 *
 * Submits to POST /api/feedback. Worker validates and writes to D1.
 * Sign-in state is read from /api/me on open; if signed in, the email
 * field is hidden and we stamp user_id server-side.
 */

import { useEffect, useState } from 'react';
import {
  Bug,
  CheckCircle,
  Lightbulb,
  Mail,
  MessageSquare,
  Send,
  User,
  X,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';

type FeedbackType = 'contact' | 'bug' | 'feature' | 'general';

interface UserContext {
  email: string;
  name: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Optional context string — e.g. the route they were on. */
  context?: string;
}

export function ContactFeedbackModal({
  isOpen,
  onClose,
  context = 'general',
}: Props) {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('contact');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [userContext, setUserContext] = useState<UserContext | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [wantsResponse, setWantsResponse] = useState(false);

  // Close on Esc.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fetch session info on open. /api/me returns 401 for anonymous;
  // treat that as "no signed-in user" and show the optional email field.
  useEffect(() => {
    if (!isOpen) return;
    setLoadingUser(true);
    (async () => {
      try {
        const me = await api<{ user: { email: string | null; display_name: string | null } }>(
          '/api/me'
        );
        if (me.user?.email) {
          setUserContext({ email: me.user.email, name: me.user.display_name });
        } else {
          setUserContext(null);
        }
      } catch {
        setUserContext(null);
      } finally {
        setLoadingUser(false);
      }
    })();
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;

    const submissionEmail = userContext?.email ?? email.trim() ?? null;
    setSubmitting(true);

    try {
      await api<{ ok: true }>('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          feedbackType,
          message: message.trim(),
          email: submissionEmail,
          name: userContext?.name ?? null,
          context,
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent : null,
          wantsResponse,
        }),
      });
      setResponseMessage(
        feedbackType === 'contact'
          ? "We've received your message and will get back to you shortly."
          : 'Thank you for your feedback. We read every submission.'
      );
      setSubmitted(true);
    } catch (err) {
      // Show a soft success on transport errors so the user doesn't
      // feel stuck. The row either landed or didn't; we'll see in D1.
      setResponseMessage(
        err instanceof ApiError && err.code === 'message_required'
          ? 'Please include a message.'
          : "Thanks for reaching out. We'll review your message."
      );
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setSubmitted(false);
    setMessage('');
    setEmail('');
    setFeedbackType('contact');
    setResponseMessage('');
    setWantsResponse(false);
  }

  function handleClose() {
    onClose();
    // Reset shortly after the close animation so reopen feels fresh.
    setTimeout(handleReset, 300);
  }

  if (!isOpen) return null;

  const placeholder =
    feedbackType === 'contact'
      ? 'How can we help you?'
      : feedbackType === 'bug'
        ? "Describe what's not working…"
        : feedbackType === 'feature'
          ? 'What would you like to see?'
          : 'Share your thoughts…';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Contact and feedback"
        className="fixed left-1/2 top-1/2 z-[9999] max-h-[90vh] w-[calc(100%-2rem)] max-w-[480px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-[color:var(--border)] bg-[color:var(--bg)] shadow-[0_25px_50px_rgba(0,0,0,0.5)]"
      >
        {submitted ? (
          <div className="px-8 py-12 text-center">
            <div
              className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border-2 border-emerald-400/80 bg-emerald-400/10 text-emerald-400"
              aria-hidden
            >
              <CheckCircle size={32} />
            </div>
            <h3 className="font-serif text-2xl italic">
              {feedbackType === 'contact' ? 'Message sent.' : 'Thank you.'}
            </h3>
            <p className="mx-auto mt-3 max-w-sm text-sm text-[color:var(--text-muted)]">
              {responseMessage}
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={handleReset}
                className="rounded-md border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-4 py-2 text-sm text-[color:var(--text)] hover:border-white/30"
              >
                Send another message
              </button>
              <button
                onClick={handleClose}
                style={{ backgroundColor: '#ffffff', color: '#000000' }}
                className="rounded-md px-4 py-2 text-sm font-medium hover:opacity-90"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 border-b border-[color:var(--border)] px-6 py-5">
              <div className="flex items-center gap-3">
                <MessageSquare
                  size={22}
                  className="text-[color:var(--text)]"
                  aria-hidden
                />
                <div>
                  <h3 className="font-serif text-lg italic">Contact &amp; feedback</h3>
                  <p className="text-xs text-[color:var(--text-muted)]">
                    Get in touch or share your thoughts
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-md text-[color:var(--text-muted)] hover:bg-white/5 hover:text-[color:var(--text)]"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-6">
              {/* Either the "sending as" line (signed in) or an optional
                  email field (signed out). Loading state hides both
                  briefly to avoid a flash. */}
              {!loadingUser && userContext && (
                <div className="flex items-center gap-2 rounded-md border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-3 py-2 text-xs">
                  <User
                    size={14}
                    className="text-[color:var(--text-muted)]"
                    aria-hidden
                  />
                  <span className="text-[color:var(--text-muted)]">Sending as:</span>
                  <span className="text-[color:var(--text)]">{userContext.email}</span>
                </div>
              )}
              {!loadingUser && !userContext && (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="feedback-email"
                    className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]"
                  >
                    Your email
                  </label>
                  <div className="relative">
                    <Mail
                      size={16}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]"
                      aria-hidden
                    />
                    <input
                      id="feedback-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-9 py-2 text-sm text-[color:var(--text)] placeholder:text-[color:var(--text-muted)] focus:border-white/30 focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-[color:var(--text-muted)]">
                    Optional, only if you&apos;d like a response.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <label className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">
                  What can we help with?
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <TypeButton
                    icon={<Mail size={18} />}
                    label="Contact"
                    active={feedbackType === 'contact'}
                    onClick={() => setFeedbackType('contact')}
                  />
                  <TypeButton
                    icon={<Bug size={18} />}
                    label="Bug"
                    active={feedbackType === 'bug'}
                    onClick={() => setFeedbackType('bug')}
                  />
                  <TypeButton
                    icon={<Lightbulb size={18} />}
                    label="Feature"
                    active={feedbackType === 'feature'}
                    onClick={() => setFeedbackType('feature')}
                  />
                  <TypeButton
                    icon={<Send size={18} />}
                    label="General"
                    active={feedbackType === 'general'}
                    onClick={() => setFeedbackType('general')}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="feedback-message"
                  className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]"
                >
                  Your message <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={placeholder}
                  rows={5}
                  required
                  maxLength={4000}
                  className="w-full resize-y rounded-md border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-3 py-2 text-sm text-[color:var(--text)] placeholder:text-[color:var(--text-muted)] focus:border-white/30 focus:outline-none"
                />
              </div>

              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={wantsResponse}
                  onChange={(e) => setWantsResponse(e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-white"
                />
                <span>I&apos;d like a response back.</span>
              </label>

              {wantsResponse && !userContext && !email.trim() && (
                <p className="text-xs text-amber-400">
                  Please provide your email above to receive a response.
                </p>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-md border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-4 py-2.5 text-sm text-[color:var(--text)] hover:border-white/30"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!message.trim() || submitting}
                  style={{ backgroundColor: '#ffffff', color: '#000000' }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition hover:opacity-90 disabled:opacity-40"
                >
                  {submitting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send message
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-[color:var(--text-muted)]">
                Questions, issues, or ideas — we read every message.
              </p>
            </form>
          </>
        )}
      </div>
    </>
  );
}

function TypeButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'flex flex-col items-center justify-center gap-1.5 rounded-md border-2 px-2 py-3 transition ' +
        (active
          ? 'border-white/60 bg-white/10'
          : 'border-[color:var(--border)] bg-[color:var(--bg-elevated)] hover:border-white/20')
      }
    >
      <span
        className={
          active ? 'text-[color:var(--text)]' : 'text-[color:var(--text-muted)]'
        }
      >
        {icon}
      </span>
      <span
        className={
          'text-xs ' +
          (active ? 'font-medium text-[color:var(--text)]' : 'text-[color:var(--text-muted)]')
        }
      >
        {label}
      </span>
    </button>
  );
}
